/**
 * Funding UI colors — sourced from `@labrav/react-components` `labravDefaultThemeV7`
 * so we do not hardcode Figma hexes that already live on the shared Labra palette.
 *
 * Prefer role-named tokens (`ctaFill`, `chartSeriesExtra`) over first-use names.
 * Only true palette gaps stay in `fundingColorOverrides`.
 */
import { labravDefaultThemeV7 } from '@labrav/react-components'
import type { Theme } from '@mui/material/styles'

/** Resolved MUI palette (module augmentation from Labra theme). */
const p = labravDefaultThemeV7.palette as Theme['palette']

/**
 * Hexes / values with no matching Labra palette token yet.
 * Prefer adding upstream to `@labrav/react-components` over growing this set.
 */
const fundingColorOverrides = {
  /** Wallet chart series — indigo not in shared palette yet */
  seriesConsumed: '#6366f1',
  reviewBorder: '#dddddd',
  /** Figma program card — closest shared is `bg.light` (#FAFAFA) */
  programCardBg: '#f8f9fa',
  scrollHintBg: '#f2f3f5',
  scrollHintBorder: '#e2e4e8',
  /** Drop shadow string, not a palette color */
  metricShadow: '0px 0px 2px rgba(0,0,0,0.12)',
  /** M3/light/error-container — archived template sticky alert (Desktop-867) */
  archiveBannerBg: '#ffdad6',
  /** Activity log / M3 error on-container */
  errorOnContainer: '#ba1a1a',
  /** Activity log success container */
  successContainer: '#c9efcd',
  successOnContainer: '#00820d',
  /** Figma Steps / section header circle-check (Desktop-1784) */
  sectionIconMuted: '#bfc8ca',
  /** Activity log neutrals */
  activityScrollbar: '#c5cdd0',
  activityRail: '#ecedef',
  activityGroupLabel: '#9aa5a8',
  activityGroupRule: '#f0f0f0',
  activityNeutralIcon: '#f6f7f9',
  activityAvatar: '#4f7de2',
  activityEventText: '#4a5568',
  activityDetailPill: '#e1e3e3',
  /** AI composer pink→purple gradient stops */
  aiGradientStart: '#ff708a',
  aiGradientMid: '#bf548e',
  aiGradientEnd: '#733495',
} as const

/** Shared Labra orange used for CTAs, chart accent, and alert borders. */
const accentOrange = p.button.secondary

export const fundingColors = {
  cardBg: p.bg.main,
  metricBg: p.bgV2.main,
  metricBorder: p.labraChat.separator,
  metricShadow: fundingColorOverrides.metricShadow,
  iconBoxBg: p.textBackground.main,
  darkText: p.labraText.tertiary,
  valueText: p.newColors.darkText,
  labelText: p.newColors.descriptionText,
  mutedText: p.labraText.description,
  divider: p.dividerV2.main,
  separator: p.labraChat.separator,
  todoRowBorder: p.labraChat.border,
  todoPillBorder: p.labraAIGradient.start,
  todoButtonBg: p.boxGradientColor.gray,
  seriesConsumed: fundingColorOverrides.seriesConsumed,
  seriesAvailable: p.listingStatus.main,
  /** Wallet / chart series accent (role-named). */
  chartSeriesExtra: accentOrange,
  /** Primary filled CTA (Save, Proceed). */
  ctaFill: accentOrange,
  /** Alert / banner border accent. */
  bannerAccent: accentOrange,
  /** @deprecated use chartSeriesExtra */
  seriesExtra: accentOrange,
  seriesTotal: p.dividerV2.main,
  warningBg: p.loading.light,
  strongText: p.labraText.primary,
  /** M3/light on-surface — note / body copy on tinted banners */
  onSurface: p.textField.dark,
  linkBlue: p.icon.blue,
  buttonBorder: p.inputBorder.main,
  reviewBorder: fundingColorOverrides.reviewBorder,
  successText: p.listingStatus.main,
  successBadgeBg: p.avatarColors.lightGreen5,
  negBadgeText: p.labraV2Errors.orange,
  stepCurrent: p.labraText.blue,
  stepUpcomingBorder: p.textField.light,
  timelineLine: p.labraChat.separator,
  dangerDot: p.error.dark,
  /** Form / field validation error text */
  errorText: p.error.main,
  /** @deprecated use bannerAccent */
  bannerBorder: accentOrange,
  programCardBg: fundingColorOverrides.programCardBg,
  eligiblePillBg: p.avatarColors.lightBlue5,
  scrollHintBg: fundingColorOverrides.scrollHintBg,
  scrollHintBorder: fundingColorOverrides.scrollHintBorder,
  tooltipLink: accentOrange,
  /** Status pill track (EnableDisableToggle) */
  statusTrack: p.select.hover,
  noteBannerBg: p.note.main,
  /** AI composer outline (Desktop-863) */
  aiCardBorder: p.textBlueV3.main,
  aiSuggestionsBorder: p.avatarColors.blue,
  archiveBannerBg: fundingColorOverrides.archiveBannerBg,
  errorOnContainer: fundingColorOverrides.errorOnContainer,
  successContainer: fundingColorOverrides.successContainer,
  successOnContainer: fundingColorOverrides.successOnContainer,
  sectionIconMuted: fundingColorOverrides.sectionIconMuted,
  activityScrollbar: fundingColorOverrides.activityScrollbar,
  activityRail: fundingColorOverrides.activityRail,
  activityGroupLabel: fundingColorOverrides.activityGroupLabel,
  activityGroupRule: fundingColorOverrides.activityGroupRule,
  activityNeutralIcon: fundingColorOverrides.activityNeutralIcon,
  activityAvatar: fundingColorOverrides.activityAvatar,
  activityEventText: fundingColorOverrides.activityEventText,
  activityDetailPill: fundingColorOverrides.activityDetailPill,
  aiGradientStart: fundingColorOverrides.aiGradientStart,
  aiGradientMid: fundingColorOverrides.aiGradientMid,
  aiGradientEnd: fundingColorOverrides.aiGradientEnd,
  onPrimary: '#ffffff',
  statusActive: '#2e7d32',
  statusMuted: '#757575',
  dropzoneBg: '#fafafa',
} as const
